import { z } from 'zod';
import { idSchema } from './content-primitives.ts';
import { formActionConditionSchema } from './form-action-conditions.ts';
import {
  integrationKindSchema,
  webhookConfigSchema,
  agencyZoomConfigSchema,
  agencyMvpConfigSchema,
  highLevelConfigSchema,
  ricochetConfigSchema,
} from './integrations.ts';

export const formActionKindSchema = z.enum(['Email', ...integrationKindSchema.options]);
export const formActionConfigSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('Email'),
    emailAddress: z.email(),
    emailToName: z.string().max(200).optional(),
    emailSubject: z.string().max(300).optional(),
  }),
  webhookConfigSchema.extend({ kind: z.literal('Webhook') }),
  agencyZoomConfigSchema.extend({ kind: z.literal('AgencyZoom') }),
  agencyMvpConfigSchema.extend({ kind: z.literal('AgencyMvp') }),
  highLevelConfigSchema.extend({ kind: z.literal('HighLevel') }),
  ricochetConfigSchema.extend({ kind: z.literal('Ricochet360') }),
]);
export const formActionSchema = z.strictObject({
  id: idSchema,
  enabled: z.boolean(),
  condition: formActionConditionSchema
    .optional()
    .describe('Comparisons are exact and case-sensitive. Choice comparisons use option values.'),
  config: formActionConfigSchema,
});
export type FormAction = z.infer<typeof formActionSchema>;

export const submissionActionFieldsSchema = formActionSchema.omit({ id: true }).extend({
  archived: z.boolean(),
});
export const submissionActionSchema = submissionActionFieldsSchema.extend({
  config: z.discriminatedUnion(
    'kind',
    formActionConfigSchema.options.map((schema) =>
      schema.extend({ kind: schema.shape.kind.meta({ readOnly: true }) }),
    ) as typeof formActionConfigSchema.options,
  ),
  id: idSchema.meta({ readOnly: true }),
  formId: idSchema.meta({ readOnly: true }),
  kind: z.literal('FormSubmissionAction').meta({ readOnly: true }),
  versionId: idSchema.meta({ readOnly: true }),
  createdAt: z.iso.datetime().meta({ pattern: undefined, readOnly: true }),
  isPublished: z.boolean().meta({ readOnly: true }),
});
export type SubmissionAction = z.infer<typeof submissionActionSchema>;
export const createSubmissionActionSchema = submissionActionFieldsSchema.extend({
  enabled: z.boolean().default(true),
  archived: z.boolean().default(false),
});
const expectedVersionId = idSchema
  .optional()
  .describe('The action version you read. Returns 409 if the fields being changed were edited since.');
export const replaceSubmissionActionSchema = submissionActionFieldsSchema.extend({
  archived: z.boolean().default(false),
  expectedVersionId,
});
export const submissionActionFields = (action: SubmissionAction) =>
  submissionActionFieldsSchema.parse({
    enabled: action.enabled,
    archived: action.archived,
    condition: action.condition,
    config: action.config,
  });
